import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import axios from 'axios';

export const STATUS_COLORS = {
  vulnerable: 'var(--danger)', likely_vulnerable: 'var(--danger)', reboot_required: 'var(--warning)',
  fixed: 'var(--success)', not_affected: 'var(--success)', unknown: 'var(--text-muted)', no_report: 'var(--text-muted)',
};
export const JOB_COLORS = {
  pending: 'var(--text-muted)', queued: 'var(--accent)', dispatched: 'var(--warning)', succeeded: 'var(--success)',
  failed: 'var(--danger)', timed_out: 'var(--danger)', cancelled: 'var(--text-muted)',
};
export const CAMPAIGN_COLORS = {
  running: 'var(--warning)', paused: 'var(--accent)', completed: 'var(--success)', aborted: 'var(--text-muted)',
};

// What each allowlisted agent action does, shown when starting a campaign.
export const ACTIONS = {
  detect: 'Re-run detection and report. Changes nothing.',
  monitor: 'Count inbound IPv4 TCP packets carrying IP options, without dropping them. Not persistent. Run before mitigate to see whether real traffic uses IP options.',
  mitigate: 'Drop inbound IPv4 TCP packets with IP options (nftables, persists across reboot) and set net.mptcp.enabled=0, tcp_migrate_req=0. Records the original values.',
  rollback: 'Undo monitor and mitigate, restoring the original sysctl values.',
  patch: 'Upgrade the kernel packages from the distro (no reboot). Fails with exit 4 when the distro has no fixed kernel yet.',
  reboot: 'Reboot in one minute.',
  'kernel-rollback': 'Make the pre-patch kernel the boot default (RHEL family only). Follow with a reboot campaign.',
};
const RISKY = new Set(['mitigate', 'patch', 'reboot', 'kernel-rollback', 'rollback']);

export function Pill({ label, color, title }) {
  const c = color || 'var(--text-muted)';
  return <span title={title} style={{ fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: `${c}22`, color: c, whiteSpace: 'nowrap' }}>{label}</span>;
}

export function StatusPill({ status }) {
  const s = status || 'no_report';
  return <Pill label={s.replace(/_/g, ' ')} color={STATUS_COLORS[s]} />;
}

// Attack signs are amber (red for kernel errors); a bare /var/crash report is shown but muted.
export function ActivityPill({ activity }) {
  if (!activity || activity === 'none') return <span style={{ color: 'var(--text-muted)' }}>none</span>;
  const color = activity === 'kernel_errors_seen' ? 'var(--danger)'
    : activity === 'ip_options_traffic_seen' ? 'var(--warning)' : 'var(--text-muted)';
  const title = activity === 'crash_report_seen'
    ? 'Something in /var/crash from the last 7 days that is not a kernel dump (on Ubuntu usually a crashed program). Not treated as an attack sign.'
    : undefined;
  return <Pill label={activity.replace(/_/g, ' ')} color={color} title={title} />;
}

// Auto-refresh that skips while the browser tab is hidden and refreshes once when it
// comes back, so an open-but-unwatched dashboard costs the server nothing.
export function useLiveRefresh(fn, ms, active = true) {
  const saved = useRef(fn);
  useEffect(() => { saved.current = fn; }, [fn]);
  useEffect(() => {
    if (!active) return undefined;
    const tick = () => { if (!document.hidden) saved.current(); };
    const t = setInterval(tick, ms);
    const onShow = () => { if (!document.hidden) saved.current(); };
    document.addEventListener('visibilitychange', onShow);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onShow); };
  }, [ms, active]);
}

export function fmtDate(v) { if (!v) return '—'; try { return new Date(v).toLocaleString(); } catch { return v; } }

export function ago(v) {
  if (!v) return 'never';
  const s = Math.round((Date.now() - new Date(v).getTime()) / 1000);
  if (s < 90) return `${s}s ago`;
  if (s < 5400) return `${Math.round(s / 60)} min ago`;
  if (s < 172800) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
}

export function errText(e, fallback) { return e?.response?.data?.error || fallback; }

export function FleetTabs() {
  const tab = ({ isActive }) => `btn ${isActive ? 'btn-primary' : 'btn-secondary'}`;
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
      <NavLink end to="/cve-fleet" className={tab}>Servers</NavLink>
      <NavLink to="/cve-fleet/campaigns" className={tab}>Campaigns</NavLink>
      <NavLink to="/cve-fleet/enrollment" className={tab}>Enrollment</NavLink>
      <NavLink to="/cve-fleet/audit" className={tab}>Audit log</NavLink>
    </div>
  );
}

export function PageHeader() {
  return (
    <div className="page-header">
      <h2>CVE-2026-98432 fleet</h2>
      <p>Linux kernel IPv4 TCP use-after-free (CVSS 9.8). Agents poll this app over outbound HTTPS; actions run in waves with a canary, and every change can be rolled back.</p>
    </div>
  );
}

// Start a campaign. `preset` may fix the selector (e.g. hosts picked in the table).
export function NewCampaignModal({ preset, tags = [], onClose, onCreated }) {
  const [form, setForm] = useState({
    action: 'detect', mode: preset?.hosts ? 'hosts' : 'all', tags: [], status: [],
    batch_size: 20, canary: 1, max_failures: 0, job_timeout: 1800, pause_after_canary: true, note: '',
  });
  // The server decides which actions are switched on (patch, reboot and
  // kernel-rollback are off until tested); offer only those.
  const [enabled, setEnabled] = useState(['detect', 'monitor', 'mitigate', 'rollback']);
  useEffect(() => {
    axios.get('/cve-fleet/summary').then(r => { if (r.data.enabled_actions) setEnabled(r.data.enabled_actions); }).catch(() => {});
  }, []);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  function selector() {
    if (form.mode === 'hosts') return { hosts: preset.hosts };
    if (form.mode === 'tags') return { tags: form.tags };
    if (form.mode === 'status') return { status: form.status };
    return { all: true };
  }
  function payload(dry) {
    return {
      action: form.action, selector: selector(), batch_size: Number(form.batch_size), canary: Number(form.canary),
      max_failures: Number(form.max_failures), job_timeout: Number(form.job_timeout),
      pause_after_canary: form.pause_after_canary, note: form.note, dry_run: dry,
    };
  }
  async function check() {
    setError(''); setPreview(null);
    try { const r = await axios.post('/cve-fleet/campaigns', payload(true)); setPreview(r.data); }
    catch (e) { setError(errText(e, 'Selector check failed')); }
  }
  async function submit(e) {
    e.preventDefault();
    if (RISKY.has(form.action) && !window.confirm(`Run "${form.action}" on ${preview ? preview.count : 'the selected'} server(s)?`)) return;
    setBusy(true); setError('');
    try { const r = await axios.post('/cve-fleet/campaigns', payload(false)); onCreated(r.data); }
    catch (err) { setError(errText(err, 'Failed to start the campaign')); }
    finally { setBusy(false); }
  }
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setPreview(null); };
  const toggle = (k, v) => set(k, form[k].includes(v) ? form[k].filter(x => x !== v) : [...form[k], v]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="modal-header"><h3>New campaign</h3></div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Action</label>
              <select className="form-input" value={form.action} onChange={e => set('action', e.target.value)}>
                {Object.keys(ACTIONS).filter(a => enabled.includes(a)).map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{ACTIONS[form.action]}</div>
            </div>
            <div className="form-group">
              <label className="form-label">Servers</label>
              <div style={{ display: 'flex', gap: 14, fontSize: 13, flexWrap: 'wrap' }}>
                {preset?.hosts && <label><input type="radio" checked={form.mode === 'hosts'} onChange={() => set('mode', 'hosts')} /> {preset.hosts.length} selected</label>}
                <label><input type="radio" checked={form.mode === 'all'} onChange={() => set('mode', 'all')} /> All enrolled</label>
                <label><input type="radio" checked={form.mode === 'tags'} onChange={() => set('mode', 'tags')} /> By tag</label>
                <label><input type="radio" checked={form.mode === 'status'} onChange={() => set('mode', 'status')} /> By status</label>
              </div>
              {form.mode === 'tags' && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8, fontSize: 13 }}>
                  {tags.length === 0 && <span style={{ color: 'var(--text-muted)' }}>No tags reported yet.</span>}
                  {tags.map(t => <label key={t}><input type="checkbox" checked={form.tags.includes(t)} onChange={() => toggle('tags', t)} /> {t}</label>)}
                </div>
              )}
              {form.mode === 'status' && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8, fontSize: 13 }}>
                  {['vulnerable', 'likely_vulnerable', 'reboot_required', 'unknown', 'fixed', 'not_affected', 'no_report'].map(s =>
                    <label key={s}><input type="checkbox" checked={form.status.includes(s)} onChange={() => toggle('status', s)} /> {s.replace(/_/g, ' ')}</label>)}
                </div>
              )}
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Canary hosts</label>
                <input className="form-input" type="number" min={0} value={form.canary} onChange={e => set('canary', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Batch size</label>
                <input className="form-input" type="number" min={1} value={form.batch_size} onChange={e => set('batch_size', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Max failures</label>
                <input className="form-input" type="number" min={0} value={form.max_failures} onChange={e => set('max_failures', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Job timeout (s)</label>
                <input className="form-input" type="number" min={60} value={form.job_timeout} onChange={e => set('job_timeout', e.target.value)} /></div>
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={form.pause_after_canary} onChange={e => set('pause_after_canary', e.target.checked)} />
              Pause after the canary wave so I can inspect it before the rest
            </label>
            <div className="form-group" style={{ marginTop: 10 }}><label className="form-label">Note (optional)</label>
              <input className="form-input" value={form.note} onChange={e => set('note', e.target.value)} placeholder="Why, ticket, change window" /></div>
            {preview && (
              <div className="card" style={{ fontSize: 13, marginTop: 8 }}>
                <strong>{preview.count}</strong> server(s) match{preview.count > 0 && <>: {preview.targets.slice(0, 12).join(', ')}{preview.count > 12 ? ` and ${preview.count - 12} more` : ''}</>}
              </div>
            )}
            {error && <div className="error-msg">{error}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-secondary" onClick={check}>Check selection</button>
            <button type="submit" className="btn btn-primary" disabled={busy || !preview || preview.count === 0}>{busy ? 'Starting…' : 'Start campaign'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
